import React, {useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';

import {Box, Button, Icon} from '@spothero/ui';
import IconArrowLeft from '@spothero/icons/arrow-left';
import IconLocationArrow from '@spothero/icons/location-arrow';

import useConfig from 'hooks/use-config';
import css from './TabbedSearchControls.module.scss';
import SearchInput from 'common/search-controls/location-input';
import SelectAirport from 'pages/home/components/select-airport';
import SearchActions from 'common/search-controls/submit-cancel-actions';
import SearchTermToggle from 'common/search-controls/search-term-toggle';
import WithSearchControls from 'common/search-controls/with-search-controls';

const TabbedSearchControls = ({
    currentSearch,
    isMobile,
    isMonthly,
    isAirport,
    isValidStart,
    isSearching,
    onInputChange,
    onFindParkingNearMe,
    onSearchClick,
    onSubmit,
    pageType,
    searchString,
}) => {
    const [showSearchModal, setShowSearchModal] = useState(false);
    const {
        initialUserData: {
            hasCloudfrontViewerGeoHeader,
            cloudfrontViewerGeolocation,
        },
    } = useConfig();
    const classes = classNames(css['TabbedSearchControls-form'], {
        'TabbedSearchControls-modal': showSearchModal,
    });
    const userLocationBias = {
        location: cloudfrontViewerGeolocation,
        radius: 16000, // in meters ~10 mi
    };

    const onToggleSearchModal = () => {
        setShowSearchModal(show => !show);
    };
    const onSearchActionClick = () => {
        if (isEmpty(currentSearch)) {
            onFindParkingNearMe();
        } else {
            onSearchClick();
        }
    };
    const onAirportInputChange = evt => {
        onInputChange(evt.target.value);
    };
    const onAirportClear = () => {
        onInputChange('');
    };
    const onAirportSelect = ({label}) => {
        onInputChange(label);

        // MP: not the most ideal way to do this but without more refactoring this seemed logical
        // allow the input change to set the "currentSearch" in WithSearchControls and then execute the search on the next UI thread available
        setTimeout(() => {
            onSearchClick();
        }, 100);
    };
    const onInputSubmit = locationData => {
        // override the submit from SearchInput to not pass true so that the search executes on selection of suggestion
        onSubmit(locationData);
    };

    useEffect(() => {
        const bodyClassNameList = document.body.classList;
        const modalBgClassName = 'no-bg-scroll';

        showSearchModal
            ? bodyClassNameList.add(modalBgClassName)
            : bodyClassNameList.remove(modalBgClassName);
    }, [showSearchModal]);

    return (
        // eslint-disable-next-line dot-notation
        <Box as="section" className={css['TabbedSearchControls']}>
            <Box display="flex" flexDirection="column" className={classes}>
                <Box
                    as="header"
                    onClick={onToggleSearchModal}
                    display={
                        showSearchModal
                            ? {base: 'inline-block', tablet: 'none'}
                            : 'none'
                    }
                    padding={4}
                    margin={-4}
                >
                    <Icon
                        as={IconArrowLeft}
                        color="gray.dark"
                        height={5}
                        width={5}
                    />
                </Box>
                <SearchTermToggle isSearching={isSearching} includeAirport />
                <Box className={css['TabbedSearchControls-input']}>
                    {isAirport ? (
                        <SelectAirport
                            focus={isMobile ? showSearchModal : true}
                            autoFocus={showSearchModal}
                            placeholder="Airport Name, Code or City"
                            searchText={currentSearch || ''}
                            showSuggestions={isMobile ? showSearchModal : true}
                            onChange={onAirportInputChange}
                            onSelect={onAirportSelect}
                            onClear={onAirportClear}
                        />
                    ) : (
                        <SearchInput
                            focus={isMobile ? showSearchModal : true}
                            autoFocus={showSearchModal}
                            isMonthly={isMonthly}
                            isSearching={isSearching}
                            searchString={searchString}
                            showLabel={false}
                            onChange={onInputChange}
                            onSubmit={onInputSubmit}
                            biasOptions={
                                hasCloudfrontViewerGeoHeader
                                    ? userLocationBias
                                    : null
                            }
                        />
                    )}
                    <SearchActions
                        isSearching={isSearching}
                        isValidStart={isValidStart}
                        onSearchClick={onSearchActionClick}
                        pageType={pageType}
                    />
                    <Box
                        className={css['TabbedSearchControls-modal-open']}
                        onClick={onToggleSearchModal}
                    />
                </Box>
                {!isAirport && (
                    <Button
                        variant="secondaryOnDark"
                        borderWidth="2px"
                        disabled={isSearching}
                        onClick={onFindParkingNearMe}
                        iconSpacing={3}
                        leftIcon={
                            <IconLocationArrow
                                width="0.875rem"
                                height="0.875rem"
                            />
                        }
                        marginY={6}
                        maxWidth={60}
                        alignSelf="center"
                    >
                        Find Parking Near Me
                    </Button>
                )}
            </Box>
        </Box>
    );
};

TabbedSearchControls.propTypes = {
    currentSearch: PropTypes.string,
    isMobile: PropTypes.bool.isRequired,
    isMonthly: PropTypes.bool,
    isAirport: PropTypes.bool,
    isValidStart: PropTypes.bool,
    isSearching: PropTypes.bool,
    onInputChange: PropTypes.func.isRequired,
    onFindParkingNearMe: PropTypes.func.isRequired,
    onSearchClick: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    pageType: PropTypes.string,
    searchString: PropTypes.string,
};

export default WithSearchControls(TabbedSearchControls);
